import { can } from '@af/shared';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useRole } from '@/lib/permissions';
import { EXTRA_GO, SHORTCUT_GROUPS } from '@/lib/shortcuts';
import { NAV } from './nav';

function Keys({ keys }: { keys: string[] }) {
  return (
    <span className="flex shrink-0 items-center gap-1">
      {keys.map((k) => (
        <kbd
          key={k}
          className="inline-flex h-6 min-w-6 items-center justify-center rounded-md border border-border bg-panel px-1.5 font-mono text-[11px] text-fg-2"
        >
          {k}
        </kbd>
      ))}
    </span>
  );
}

function Group({ title, rows }: { title: string; rows: { keys: string[]; label: string }[] }) {
  return (
    <section>
      <h3 className="mb-1.5 text-xs font-medium text-faint">{title}</h3>
      <ul className="flex flex-col">
        {rows.map((r) => (
          <li key={r.label} className="flex h-8 items-center justify-between gap-3 text-sm text-fg-2">
            <span className="truncate">{r.label}</span>
            <Keys keys={r.keys} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** `?` overlay: every global shortcut, launcher key and `g <key>` destination. */
export function ShortcutsOverlay({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation();
  const role = useRole();
  const nav = [
    ...NAV.filter((n) => !n.perm || can(role, n.perm)).map((n) => ({
      keys: ['g', n.hotkey],
      label: t(n.key),
    })),
    ...EXTRA_GO.map((g) => ({ keys: ['g', g.hotkey], label: t(g.label) })),
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('nav.shortcuts')} description={t('shortcuts.spaceNote')} className="max-w-2xl">
        <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
          <div className="flex flex-col gap-5">
            {SHORTCUT_GROUPS.map((g) => (
              <Group
                key={g.title}
                title={t(g.title)}
                rows={g.items.map((i) => ({ keys: i.keys, label: t(i.label) }))}
              />
            ))}
          </div>
          <Group title={t('shortcuts.goTo')} rows={nav} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
